/**
 * Bộ mẫu phân quyền theo VỊ TRÍ CÔNG VIỆC — đổ sẵn bảng tick khi admin tạo tài khoản.
 *
 * Nguồn: bảng "ERP User Access Rights" khách gửi 16/09/2026 (10 vị trí × 30
 * điểm quyền), đối chiếu với thang chuẩn của các ERP phổ biến rồi chỉnh lại
 * ở những chỗ bảng của khách tự mâu thuẫn hoặc trái chuẩn:
 *
 *  - Thang hành động View → Create → Edit → Full là của NetSuite; Odoo dùng
 *    read/create/write/unlink. Cấp "xoá" chỉ giao cho trưởng bộ phận, tài
 *    chính và ban giám đốc (đúng ghi chú 5 của chính khách: "xoá … chỉ cấp
 *    cho quản lý / tài chính / ban giám đốc").
 *  - "Duyệt" là ô riêng, theo cách SAP tách mã duyệt (M_EINK_FRG) khỏi quyền
 *    sửa và Business Central có bảng người duyệt riêng. Người lập chứng từ
 *    KHÔNG có ô duyệt trên chính chứng từ đó (nguyên tắc 3 của khách).
 *  - Trưởng bộ phận BAO TRÙM nhân viên của mình (Odoo: Purchase Manager kế
 *    thừa Purchase User). Bảng khách gửi có chỗ ngược lại (nhân viên mua được
 *    xoá NCC mà trưởng phòng mua thì không) — đó là lỗi điền, không phải ý.
 *  - Kế toán và Người xem KHÔNG quản lý tài khoản / cấu hình quyền. Mọi ERP
 *    chuẩn đều giữ hai việc đó cho quản trị viên; hàng Viewer ✓ ở cột đó trong
 *    bảng khách là do quy ước "Read-Only cũng đánh ✓" của họ.
 *
 * Trang đã ẩn (`hidden` trong MENUS, ví dụ Cơ hội bán hàng từ 16/09/2026) không có
 * trong mẫu nào — mở lại trang thì thêm lại vào mẫu.
 *
 * Đây là dữ liệu thuần: không có quy tắc nào ở đây, chỉ có bảng tick. Quy tắc
 * chặn nằm ở `access.ts`. Tên hiển thị ở `i18n/userAccess.ts` (`templates.*`).
 * Mẫu chỉ là ĐIỂM XUẤT PHÁT: admin đổ sẵn rồi tick thêm/bớt cho từng người.
 */

import { MENUS, type AccessVerb, type CustomerScope } from "./access";

export interface AccessTemplate {
  key: string;
  grants: Readonly<Record<string, readonly AccessVerb[]>>;
  canViewCost: boolean;
  customerScope: CustomerScope;
}

const V: readonly AccessVerb[] = ["view"];
const C: readonly AccessVerb[] = ["view", "create"];
const E: readonly AccessVerb[] = ["view", "create", "update"];
const EA: readonly AccessVerb[] = ["view", "create", "update", "approve"];
const F: readonly AccessVerb[] = ["view", "create", "update", "delete"];
const FA: readonly AccessVerb[] = ["view", "create", "update", "delete", "approve"];
/** Chỉ duyệt, không sửa nháp — kiểm toán viên ký chứ không tự làm sổ. */
const VA: readonly AccessVerb[] = ["view", "approve"];

/** Mọi menu đang hiển thị — trang đã ẩn (`hidden`) không vào mẫu "mọi màn hình"; dòng chỉ có vài ô (`verbs`) chỉ nhận đúng các ô đó. */
const every = (verbs: readonly AccessVerb[]): Record<string, readonly AccessVerb[]> =>
  Object.fromEntries(MENUS.filter((m) => !m.hidden).map((m) => [m.key, m.verbs ? verbs.filter((v) => m.verbs!.includes(v)) : verbs]));

export const ACCESS_TEMPLATES: readonly AccessTemplate[] = [
  /** Ban giám đốc: mọi màn hình, mọi hành động, thấy giá vốn. Chỉ khác admin ở chỗ vẫn là một bảng tick sửa được. */
  { key: "generalManager", canViewCost: true, customerScope: "all", grants: every(FA) },

  /** Trưởng phòng mua: toàn quyền NCC và chuỗi mua hàng, duyệt PO/yêu cầu mua/sourcing; nhìn được nhu cầu bán để mua. */
  {
    key: "purchasingSupervisor", canViewCost: false, customerScope: "all",
    grants: {
      dashboard: V, analytics: V, piStatus: V, orderProcess: V, kpis: V,
      products: E, customers: V, customerTerms: V, suppliers: F, unitPricing: V,
      quotations: V, salesOrders: V, proformaInvoices: V,
      mrp: E, requisitions: FA, sourcing: FA, purchaseOrders: FA, supplierClaims: EA,
      warehouse: V, warehouseLocations: V, stockMoves: V,
      boms: V, production: V,
    },
  },

  /** Nhân viên mua: lập và sửa PO/yêu cầu mua, KHÔNG duyệt, KHÔNG xoá; thêm/sửa NCC nhưng không xoá. */
  {
    key: "purchaser", canViewCost: false, customerScope: "all",
    grants: {
      dashboard: V, piStatus: V, orderProcess: V,
      products: V, customers: V, customerTerms: V, suppliers: E,
      quotations: V, salesOrders: V, proformaInvoices: V,
      mrp: V, requisitions: E, sourcing: E, purchaseOrders: E, supplierClaims: E,
      warehouse: V, warehouseLocations: V, stockMoves: V,
      boms: V, production: V,
    },
  },

  /** Trưởng phòng kinh doanh: toàn quyền chuỗi bán hàng và khách hàng, duyệt SO; thấy mọi khách. */
  {
    key: "salesSupervisor", canViewCost: false, customerScope: "all",
    grants: {
      dashboard: V, analytics: V, piStatus: V, orderProcess: V, kpis: V,
      products: V, customers: F, customerTerms: E, unitPricing: V,
      sampleOrders: FA, sampleCards: FA,
      quotations: FA, salesOrders: FA, proformaInvoices: FA, invoices: FA, serviceTickets: FA,
      packingLists: E, deliveryNotes: E,
      warehouse: V, warehouseLocations: V, stockMoves: V,
      production: V, pProcess: E,
      statement: V,
    },
  },

  /** Nhân viên theo đơn: lập báo giá/PI/SO và ghi thu tiền cho KHÁCH ĐƯỢC GIAO; không duyệt, không xoá. */
  {
    key: "orderFollower", canViewCost: false, customerScope: "assigned",
    grants: {
      dashboard: V, piStatus: V, orderProcess: V,
      products: V, customers: E, customerTerms: V,
      sampleOrders: E, sampleCards: E,
      quotations: E, salesOrders: E, proformaInvoices: E, invoices: E, serviceTickets: E,
      packingLists: E, deliveryNotes: E,
      warehouse: V, warehouseLocations: V, stockMoves: V,
      production: V, pProcess: E,
      statement: V,
    },
  },

  /** Tài chính / kế toán: sổ cái, hoá đơn, công nợ, thu tiền, giá vốn và lợi nhuận; duyệt kiểm kê; KHÔNG quản lý tài khoản. */
  {
    key: "finance", canViewCost: true, customerScope: "all",
    grants: {
      dashboard: V, analytics: V, profit: V, piStatus: V, orderProcess: V, kpis: V,
      products: V, customers: E, customerTerms: E, suppliers: E, unitPricing: V,
      quotations: V, salesOrders: V, proformaInvoices: E, invoices: F, serviceTickets: V,
      requisitions: V, purchaseOrders: V, supplierClaims: V,
      warehouse: V, warehouseLocations: V, stockMoves: VA, packingLists: V, deliveryNotes: V,
      statement: V, finance: FA, glAccounts: F,
      settings: E, deletedRecords: V,
    },
  },

  /**
   * Thủ kho: nhập/xuất, xác nhận phiếu nhập, kiểm kê (lập, không áp số), đóng gói và giao; chỉ xem sản phẩm.
   * Quét & tem (xem): màn Quét theo việc (18/09/2026) làm cho chính thủ kho — thiếu ô này thì thanh bên không có lối vào.
   * Chỉ cần xem: các việc trên màn quét gọi API của Kho & LOT, Biến động kho, Phiếu giao và bị chặn theo đúng ô của các menu đó.
   */
  {
    key: "warehouseKeeper", canViewCost: false, customerScope: "all",
    grants: {
      dashboard: V,
      products: V,
      warehouse: EA, warehouseLocations: EA, stockMoves: E, packingLists: E, deliveryNotes: E, devices: V,
      production: V, pProcess: V,
    },
  },

  /**
   * Kiểm tra chất lượng: IQC và hồ sơ chất lượng, duyệt kết quả kiểm; chỉ xem kho và sản phẩm.
   * Hậu mãi (xem) từ 20/09/2026: khiếu nại nhà cung cấp lập TỪ hồ sơ khiếu nại của khách — bộ phận chất lượng điều tra nguyên nhân
   * khiếu nại khách rồi đòi nhà cung cấp (SAP QM: thông báo Q1 của khách → Q2 với nhà cung cấp). Thiếu ô này thì không mở được hồ sơ
   * khách để bấm "Lập khiếu nại nhà cung cấp". Chỉ xem: nguyên nhân trên hồ sơ khách vẫn do người có ô Sửa ở Hậu mãi ghi.
   */
  {
    key: "qualityInspector", canViewCost: false, customerScope: "all",
    grants: {
      dashboard: V,
      products: V,
      serviceTickets: V,
      warehouse: EA, warehouseLocations: EA, stockMoves: V,
      production: V, quality: EA, supplierClaims: E,
    },
  },

  /** Người xem: chỉ đọc các màn hình nghiệp vụ; không giá vốn, không tài chính, không quản trị. */
  {
    key: "viewer", canViewCost: false, customerScope: "all",
    grants: {
      dashboard: V, analytics: V, piStatus: V, orderProcess: V,
      products: V, customers: V, customerTerms: V, suppliers: V,
      sampleOrders: V, sampleCards: V,
      quotations: V, salesOrders: V, proformaInvoices: V, invoices: V, serviceTickets: V,
      requisitions: V, purchaseOrders: V,
      warehouse: V, warehouseLocations: V, stockMoves: V, packingLists: V, deliveryNotes: V,
      production: V, quality: V, pProcess: V,
      statement: V,
    },
  },
];

export const ACCESS_TEMPLATE_KEYS: readonly string[] = ACCESS_TEMPLATES.map((t) => t.key);

export function accessTemplate(key: string): AccessTemplate | undefined {
  return ACCESS_TEMPLATES.find((t) => t.key === key);
}
